import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiJson } from "../utils/fetch";
import type { Task, TaskLabel, TaskStatus, TimelineEntry } from "../types";

export function useTasks(
  projectId: string,
  filters?: {
    statuses?: TaskStatus[];
    labels?: TaskLabel[];
    blocked?: boolean;
  },
) {
  const qs = new URLSearchParams();
  for (const s of filters?.statuses ?? []) qs.append("status", s);
  for (const l of filters?.labels ?? []) qs.append("label", l);
  if (filters?.blocked !== undefined)
    qs.set("blocked", String(filters.blocked));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return useQuery({
    queryKey: ["projects", projectId, "tasks", filters],
    queryFn: () => apiJson<Task[]>(`/projects/${projectId}/tasks${suffix}`),
    enabled: Boolean(projectId),
    refetchOnWindowFocus: false,
    refetchInterval: 15000,
  });
}

export function useCreateTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      description?: string;
      label: TaskLabel;
      note?: string;
      depends_on?: string[];
      position?: number;
    }) =>
      apiJson<Task>(`/projects/${projectId}/tasks`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "tasks"] });
      qc.invalidateQueries({ queryKey: ["users"], predicate: (q) => q.queryKey.includes("billing") });
      qc.invalidateQueries({ queryKey: ["orgs"], predicate: (q) => q.queryKey.includes("billing") });
    },
  });
}

export function useTimeline(
  taskId: string,
  limit?: { tail?: number; head?: number },
) {
  const qs = new URLSearchParams();
  if (limit?.tail) qs.set("tail", String(limit.tail));
  if (limit?.head) qs.set("head", String(limit.head));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return useQuery({
    queryKey: ["tasks", taskId, "timeline", limit],
    queryFn: () =>
      apiJson<TimelineEntry[]>(`/tasks/${taskId}/timeline${suffix}`),
    enabled: Boolean(taskId),
    refetchInterval: 15000,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateTask(taskId: string, projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      status?: TaskStatus;
      claimed_by?: string | null;
      note?: string;
      commit_hash?: string;
      description?: string;
      position?: number;
      add_deps?: string[];
      remove_deps?: string[];
    }) =>
      apiJson<Task>(`/tasks/${taskId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks", taskId] });
      if (projectId)
        qc.invalidateQueries({ queryKey: ["projects", projectId, "tasks"] });
    },
  });
}

export function useReorderTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, position }: { taskId: string; position: number }) =>
      apiJson<Task>(`/tasks/${taskId}`, {
        method: "PATCH",
        body: JSON.stringify({ position }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "tasks"] });
    },
  });
}
